// Home, last: registered voters per county counted two ways — by the
// county that issued their ID (NRB) and by the county they vote in (IEBC
// roll) — with a grand total, filtered by sex or tribe.
// The API breaks figures down by one of these at a time, so picking one
// clears the others. Diaspora and Prisons are pending (see VOTING_ONLY):
// their area of ID issuance reads "Still receiving updates"; area of voting
// keeps the IEBC figure so its total matches the Registered voters page.

import React, { useMemo, useState } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { IdDemoBy } from '@services/api/analytics';
import { areaName } from './data';
import { emptyIebc, emptyNrb, isVotingOnly, PENDING_KEY, pendingName, useIdPivot, useIdRows, useRegPivot, useRegRows } from './agg';
import { C, Column, DataTable, DateFilterValue, Dropdown, Panel, PENDING_TEXT } from './ui';

const ALL: DateFilterValue = { preset: 'all', from: '', to: '' };

/** NRB and IEBC spell some counties differently (HOMABAY / HOMA BAY,
 *  TAITA/TAVETA / TAITA TAVETA, MURANGA / MURANG'A): compare letters only. */
const ALIASES: Record<string, string> = { NAIROBICITY: 'NAIROBI' };
const norm = (s: string) => {
  const k = (s || '').toUpperCase().replace(/[^A-Z]/g, '');
  return ALIASES[k] || k;
};

interface Row { key: string; name: string; byId: number; byVote: number }

type By = Extract<IdDemoBy, 'sex' | 'tribe'>;
const BY: { by: By; label: string }[] = [
  { by: 'sex', label: 'Sex' },
  { by: 'tribe', label: 'Tribe' },
];

export const CountyComparison: React.FC<{ onCounty?: (c: string) => void }> = ({ onCounty }) => {
  const [pick, setPick] = useState<{ by: By; value: string } | null>(null);
  const by = pick?.by ?? 'sex';

  // no filter: the county totals
  const idRows = useIdRows(emptyNrb, ALL);
  const regRows = useRegRows(emptyIebc, ALL);
  // a filter: the county x sex / tribe pivots (the options come from them too)
  const idSex = useIdPivot(emptyNrb, 'sex', true);
  const idTribe = useIdPivot(emptyNrb, 'tribe', true);
  const regSex = useRegPivot(emptyIebc, ALL, 'county', 'sex');
  const regTribe = useRegPivot(emptyIebc, ALL, 'county', 'tribe');
  const idPivot = { sex: idSex, tribe: idTribe }[by].pivot;
  const regPivot = { sex: regSex, tribe: regTribe }[by].pivot;

  const options = (b: By) => {
    const id = { sex: idSex, tribe: idTribe }[b].pivot?.columns ?? [];
    const reg = { sex: regSex, tribe: regTribe }[b].pivot?.columns ?? [];
    const seen = new Map<string, string>();
    [...id, ...reg].forEach((c) => { if (!seen.has(norm(c))) seen.set(norm(c), c); });
    const list = [...seen.values()];
    return b === 'tribe' ? list.sort((a, z) => a.localeCompare(z)) : list;
  };

  const rows: Row[] = useMemo(() => {
    const out = new Map<string, Row>();
    const row = (name: string) => {
      const k = norm(name);
      if (!out.has(k)) out.set(k, { key: name, name, byId: 0, byVote: 0 });
      return out.get(k)!;
    };
    if (!pick) {
      idRows.rows.forEach((r) => { row(r.name).byId += r.registered; });
      regRows.rows.forEach((r) => { row(r.name).byVote += r.registered; });
    } else {
      const want = norm(pick.value);
      const j = (idPivot?.columns ?? []).findIndex((c) => norm(c) === want);
      if (j >= 0) idPivot?.rows.forEach((r) => { row(r.key).byId += r.registered[j]; });
      const k = (regPivot?.columns ?? []).findIndex((c) => norm(c) === want);
      if (k >= 0) regPivot?.rows.forEach((r) => { row(r.key).byVote += r.values[k]; });
    }
    const list = [...out.values()].filter((r) => r.name);
    // ID-issuance rows leave out app registrations; the difference to the
    // roll goes in one row, so both columns total the same
    const gap = list.reduce((s, r) => s + r.byVote - r.byId, 0);
    if (gap && list.length) list.push({ key: PENDING_KEY, name: pick ? 'Difference to the IEBC roll' : pendingName(gap), byId: gap, byVote: 0 });
    return list;
  }, [pick, idRows.rows, regRows.rows, idPivot, regPivot]);
  const pinned = (r: Row) => r.key === PENDING_KEY || isVotingOnly(r.name);

  const fetching = pick
    ? { sex: idSex, tribe: idTribe }[by].fetching || { sex: regSex, tribe: regTribe }[by].fetching
    : idRows.fetching || regRows.fetching;
  const loading = pick ? !idPivot || !regPivot : idRows.loading || regRows.loading;

  const columns: Column<Row>[] = [
    { key: 'name', label: 'County', value: (r) => areaName(r.name) },
    { key: 'byId', label: 'Registered as per area of ID issuance', value: (r) => (isVotingOnly(r.name) ? PENDING_TEXT : r.byId), kind: 'num', color: C.blue },
    { key: 'byVote', label: 'Registered as per area of voting', value: (r) => (r.key === PENDING_KEY ? '—' : r.byVote), kind: 'num', color: C.green },
  ];

  return (
    <Panel sx={{ height: 'auto' }}
      title={
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', py: 0.5 }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mr: 1, mt: 2.5 }}>Registered voters by county</Typography>
          {BY.map(({ by: b, label }) => (
            <Dropdown key={b} label={label} minWidth={b === 'tribe' ? 200 : 150}
              value={pick?.by === b ? pick.value : ''}
              options={options(b).map((v) => ({ value: v, label: v }))}
              placeholder={`All (${label.toLowerCase()})`}
              onChange={(v) => setPick(v ? { by: b, value: v } : null)} />
          ))}
          <Typography sx={{ fontSize: '0.68rem', color: C.muted, mt: 3 }}>
            {pick ? `${pick.value} only` : 'All registered voters'} · one filter at a time · area of ID issuance from NRB, area of voting from the IEBC roll
          </Typography>
        </Box>
      }>
      {fetching && <LinearProgress sx={{ height: 2, backgroundColor: 'transparent', '& .MuiLinearProgress-bar': { backgroundColor: C.green } }} />}
      <Box sx={{ height: 520 }}>
        <DataTable rows={rows} columns={columns} rowKey={(r) => r.key} maxHeight={470} totalLabel="GRAND TOTAL"
          onRowClick={onCounty ? (r) => { if (!pinned(r)) onCounty(r.key); } : undefined} pinLast={pinned}
          fileName={`registered-by-county${pick ? `-${pick.value}` : ''}`}
          searchPlaceholder="Search county…" emptyText={loading ? 'Loading…' : 'No rows'} />
      </Box>
    </Panel>
  );
};
